import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  useMedicalProviders, 
  addMedicalProvider, 
  updateMedicalProvider, 
  deleteMedicalProvider 
} from '../../lib/firebase/queries/medicalProviders';
import {
  useTreatmentCategories,
  addTreatmentCategory,
  updateTreatmentCategory,
  deleteTreatmentCategory
} from '../../lib/firebase/queries/treatmentCategories';
import { MedicalReportProvider, TreatmentCategory } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Loader2,
  Building2,
  FileText,
  Layers,
  Settings,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';

interface CoordinatorSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CoordinatorSettings({ isOpen, onClose }: CoordinatorSettingsProps) {
  const { t, dir } = useLanguage();
  const { providers, loading: providersLoading } = useMedicalProviders();
  const { categories, loading: categoriesLoading } = useTreatmentCategories();
  
  const [mainTab, setMainTab] = useState('providers');
  
  // Provider form state
  const [isProviderEditOpen, setIsProviderEditOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<MedicalReportProvider | null>(null);
  const [isProviderSubmitting, setIsProviderSubmitting] = useState(false);
  const [providerActiveTab, setProviderActiveTab] = useState('details');
  const [providerFormData, setProviderFormData] = useState({
    name: '',
    nameAr: '',
    licenseNumber: '',
    vatNumber: '',
    iban: '',
    cashbackPercentage: '',
    isActive: true,
  });
  const [departments, setDepartments] = useState<string[]>([]);
  const [newDepartment, setNewDepartment] = useState('');

  // Category form state
  const [isCategoryEditOpen, setIsCategoryEditOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TreatmentCategory | null>(null);
  const [isCategorySubmitting, setIsCategorySubmitting] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    nameAr: '',
    description: '',
    isActive: true,
  });

  // Provider functions
  const resetProviderForm = () => {
    setProviderFormData({
      name: '',
      nameAr: '',
      licenseNumber: '',
      vatNumber: '',
      iban: '',
      cashbackPercentage: '',
      isActive: true,
    });
    setDepartments([]);
    setNewDepartment('');
    setEditingProvider(null);
    setProviderActiveTab('details');
  };

  const openAddProviderDialog = () => {
    resetProviderForm();
    setIsProviderEditOpen(true);
  };

  const openEditProviderDialog = (provider: MedicalReportProvider) => {
    setEditingProvider(provider);
    setProviderFormData({
      name: provider.name,
      nameAr: provider.nameAr || '',
      licenseNumber: provider.licenseNumber,
      vatNumber: provider.vatNumber || '',
      iban: provider.iban || '',
      cashbackPercentage: provider.cashbackPercentage != null ? String(provider.cashbackPercentage) : '',
      isActive: provider.isActive,
    });
    setDepartments([...provider.departments]);
    setProviderActiveTab('details');
    setIsProviderEditOpen(true);
  };

  const handleAddDepartment = () => {
    const trimmed = newDepartment.trim();
    if (trimmed && !departments.includes(trimmed)) {
      setDepartments([...departments, trimmed]);
      setNewDepartment('');
    }
  };

  const handleRemoveDepartment = (index: number) => {
    setDepartments(departments.filter((_, i) => i !== index));
  };

  const handleProviderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!providerFormData.name || !providerFormData.nameAr || !providerFormData.licenseNumber) {
      toast.error(t('dashboard.coordinator.settings.providers.validation.required'));
      setProviderActiveTab('details');
      return;
    }

    setIsProviderSubmitting(true);
    try {
      const providerData = {
        name: providerFormData.name,
        nameAr: providerFormData.nameAr,
        licenseNumber: providerFormData.licenseNumber,
        vatNumber: providerFormData.vatNumber,
        iban: providerFormData.iban,
        cashbackPercentage: providerFormData.cashbackPercentage ? Number(providerFormData.cashbackPercentage) : undefined,
        departments: departments,
        isActive: providerFormData.isActive,
      };

      if (editingProvider) {
        await updateMedicalProvider(editingProvider.id, providerData);
        toast.success(t('dashboard.coordinator.settings.providers.updateSuccess'));
      } else {
        await addMedicalProvider(providerData);
        toast.success(t('dashboard.coordinator.settings.providers.addSuccess'));
      }

      setIsProviderEditOpen(false);
      resetProviderForm();
    } catch (error) {
      console.error('Error saving provider:', error);
      toast.error(t('dashboard.coordinator.settings.providers.saveError'));
    } finally {
      setIsProviderSubmitting(false);
    }
  };

  const handleDeleteProvider = async (provider: MedicalReportProvider) => {
    if (!confirm(t('dashboard.coordinator.settings.providers.confirmDelete'))) return;
    
    try {
      await deleteMedicalProvider(provider.id);
      toast.success(t('dashboard.coordinator.settings.providers.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error(t('dashboard.coordinator.settings.providers.deleteError'));
    }
  };

  // Category functions
  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      nameAr: '',
      description: '',
      isActive: true,
    });
    setEditingCategory(null);
  };

  const openAddCategoryDialog = () => {
    resetCategoryForm();
    setIsCategoryEditOpen(true);
  };

  const openEditCategoryDialog = (category: TreatmentCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      nameAr: category.nameAr || '',
      description: category.description || '',
      isActive: category.isActive,
    });
    setIsCategoryEditOpen(true);
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryFormData.name || !categoryFormData.nameAr) {
      toast.error(t('dashboard.coordinator.settings.categories.validation.required'));
      return;
    }

    setIsCategorySubmitting(true);
    try {
      const categoryData = {
        name: categoryFormData.name,
        nameAr: categoryFormData.nameAr,
        description: categoryFormData.description,
        isActive: categoryFormData.isActive,
      };

      if (editingCategory) {
        await updateTreatmentCategory(editingCategory.id, categoryData);
        toast.success(t('dashboard.coordinator.settings.categories.updateSuccess'));
      } else {
        await addTreatmentCategory(categoryData);
        toast.success(t('dashboard.coordinator.settings.categories.addSuccess'));
      }

      setIsCategoryEditOpen(false);
      resetCategoryForm();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(t('dashboard.coordinator.settings.categories.saveError'));
    } finally {
      setIsCategorySubmitting(false);
    }
  };

  const handleDeleteCategory = async (category: TreatmentCategory) => {
    if (!confirm(t('dashboard.coordinator.settings.categories.confirmDelete'))) return;
    
    try {
      await deleteTreatmentCategory(category.id);
      toast.success(t('dashboard.coordinator.settings.categories.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(t('dashboard.coordinator.settings.categories.deleteError'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader dir={dir}>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t('dashboard.coordinator.settings.title')}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="providers" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t('dashboard.coordinator.settings.providersTab')}
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              {t('dashboard.coordinator.settings.categoriesTab')}
            </TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers" className="mt-0">
            <div className="space-y-4">
              <div className="flex justify-start">
                <Button onClick={openAddProviderDialog}>
                  <Plus className="h-4 w-4 me-2" />
                  {t('dashboard.coordinator.settings.providers.addProvider')}
                </Button>
              </div>

              {providersLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : providers.length === 0 ? (
                <div className="text-center p-8 text-muted-foreground">
                  {t('dashboard.coordinator.settings.providers.noProviders')}
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table dir={dir}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.providers.tableName')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.providers.licenseNumber')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.providers.departments')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.providers.status')}</TableHead>
                        <TableHead className="text-end">{t('dashboard.coordinator.settings.providers.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providers.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell className="text-start">
                            <div className="font-medium">
                              {dir === 'rtl' ? (provider.nameAr || provider.name) : provider.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-start"><span dir="ltr">{provider.licenseNumber}</span></TableCell>
                          <TableCell className="text-start">
                            <div className="flex flex-wrap gap-1">
                              {provider.departments.slice(0, 2).map((dept, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {dept}
                                </Badge>
                              ))}
                              {provider.departments.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{provider.departments.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-start">
                            <Badge variant={provider.isActive ? 'default' : 'secondary'}>
                              {provider.isActive 
                                ? t('dashboard.coordinator.settings.active') 
                                : t('dashboard.coordinator.settings.inactive')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-end">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditProviderDialog(provider)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteProvider(provider)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-0">
            <div className="space-y-4">
              <div className="flex justify-start">
                <Button onClick={openAddCategoryDialog}>
                  <Plus className="h-4 w-4 me-2" />
                  {t('dashboard.coordinator.settings.categories.addCategory')}
                </Button>
              </div>

              {categoriesLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center p-8 text-muted-foreground">
                  {t('dashboard.coordinator.settings.categories.noCategories')}
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table dir={dir}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.categories.tableName')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.categories.description')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.coordinator.settings.categories.status')}</TableHead>
                        <TableHead className="text-end">{t('dashboard.coordinator.settings.categories.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category) => (
                        <TableRow key={category.id}>
                          <TableCell className="text-start">
                            <div className="font-medium">
                              {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-start max-w-xs truncate">
                            {category.description || '-'}
                          </TableCell>
                          <TableCell className="text-start">
                            <Badge variant={category.isActive ? 'default' : 'secondary'}>
                              {category.isActive 
                                ? t('dashboard.coordinator.settings.active') 
                                : t('dashboard.coordinator.settings.inactive')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-end">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditCategoryDialog(category)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(category)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Add/Edit Provider Dialog */}
      <Dialog open={isProviderEditOpen} onOpenChange={(open) => { setIsProviderEditOpen(open); if (!open) resetProviderForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle>
              {editingProvider 
                ? t('dashboard.coordinator.settings.providers.editProvider') 
                : t('dashboard.coordinator.settings.providers.addProvider')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleProviderSubmit}>
            <Tabs value={providerActiveTab} onValueChange={setProviderActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="details" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('dashboard.coordinator.settings.providers.detailsTab')}
                </TabsTrigger>
                <TabsTrigger value="departments" className="flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  {t('dashboard.coordinator.settings.providers.departmentsTab')}
                  {departments.length > 0 && (
                    <Badge variant="secondary" className="ms-1 h-5 px-1.5">{departments.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="providerName">{t('dashboard.coordinator.settings.providers.name')} *</Label>
                    <Input
                      id="providerName"
                      value={providerFormData.name}
                      onChange={(e) => setProviderFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('dashboard.coordinator.settings.providers.namePlaceholder')}
                      required
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="providerNameAr">{t('dashboard.coordinator.settings.providers.nameAr')} *</Label>
                    <Input
                      id="providerNameAr"
                      value={providerFormData.nameAr}
                      onChange={(e) => setProviderFormData(prev => ({ ...prev, nameAr: e.target.value }))}
                      placeholder={t('dashboard.coordinator.settings.providers.nameArPlaceholder')}
                      required
                      dir="rtl"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">{t('dashboard.coordinator.settings.providers.licenseNumber')} *</Label>
                    <Input
                      id="licenseNumber"
                      value={providerFormData.licenseNumber}
                      onChange={(e) => setProviderFormData(prev => ({ ...prev, licenseNumber: e.target.value }))}
                      required
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vatNumber">{t('dashboard.coordinator.settings.providers.vatNumber')}</Label>
                    <Input
                      id="vatNumber"
                      value={providerFormData.vatNumber}
                      onChange={(e) => setProviderFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                      dir="ltr"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="iban">{t('dashboard.coordinator.settings.providers.iban')}</Label>
                    <Input
                      id="iban"
                      value={providerFormData.iban}
                      onChange={(e) => setProviderFormData(prev => ({ ...prev, iban: e.target.value }))}
                      placeholder={t('dashboard.coordinator.settings.providers.ibanPlaceholder')}
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cashbackPercentage">{t('dashboard.coordinator.settings.providers.cashbackPercentage')}</Label>
                    <Input
                      id="cashbackPercentage"
                      type="number"
                      min="0"
                      max="100"
                      value={providerFormData.cashbackPercentage}
                      onChange={(e) => setProviderFormData(prev => ({ ...prev, cashbackPercentage: e.target.value }))}
                      placeholder={t('dashboard.coordinator.settings.providers.cashbackPercentagePlaceholder')}
                      dir="ltr"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="providerIsActive"
                    checked={providerFormData.isActive}
                    onChange={(e) => setProviderFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="providerIsActive" className="cursor-pointer">
                    {t('dashboard.coordinator.settings.providers.isActive')}
                  </Label>
                </div>
              </TabsContent>

              <TabsContent value="departments" className="space-y-4 mt-0">
                <div className="flex gap-2">
                  <Input
                    value={newDepartment}
                    onChange={(e) => setNewDepartment(e.target.value)}
                    placeholder={t('dashboard.coordinator.settings.providers.departmentsPlaceholder')}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDepartment(); } }}
                    dir={dir}
                    className="flex-1"
                  />
                  <Button type="button" onClick={handleAddDepartment} disabled={!newDepartment.trim()} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  {departments.length === 0 ? (
                    <div className="text-center p-6 text-muted-foreground text-sm">
                      <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      {t('dashboard.coordinator.settings.providers.noDepartments')}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {departments.map((dept, index) => (
                        <div key={index} className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-medium">
                              {index + 1}
                            </div>
                            <span className="font-medium">{dept}</span>
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleRemoveDepartment(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsProviderEditOpen(false)} disabled={isProviderSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isProviderSubmitting}>
                {isProviderSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {editingProvider ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={isCategoryEditOpen} onOpenChange={(open) => { setIsCategoryEditOpen(open); if (!open) resetCategoryForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle>
              {editingCategory 
                ? t('dashboard.coordinator.settings.categories.editCategory') 
                : t('dashboard.coordinator.settings.categories.addCategory')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="categoryName">{t('dashboard.coordinator.settings.categories.name')} *</Label>
                <Input
                  id="categoryName"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('dashboard.coordinator.settings.categories.namePlaceholder')}
                  required
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoryNameAr">{t('dashboard.coordinator.settings.categories.nameAr')} *</Label>
                <Input
                  id="categoryNameAr"
                  value={categoryFormData.nameAr}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, nameAr: e.target.value }))}
                  placeholder={t('dashboard.coordinator.settings.categories.nameArPlaceholder')}
                  required
                  dir="rtl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoryDescription">{t('dashboard.coordinator.settings.categories.description')}</Label>
              <Textarea
                id="categoryDescription"
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('dashboard.coordinator.settings.categories.descriptionPlaceholder')}
                rows={2}
                dir={dir}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="categoryIsActive"
                checked={categoryFormData.isActive}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="categoryIsActive" className="cursor-pointer">
                {t('dashboard.coordinator.settings.categories.isActive')}
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCategoryEditOpen(false)} disabled={isCategorySubmitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isCategorySubmitting}>
                {isCategorySubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {editingCategory ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// Keep the old export for backward compatibility
export { CoordinatorSettings as MedicalProvidersSettings };
